import {
  ConversationEvent,
  PresenceEvent,
  SOCKET_PATH,
  SOCKET_READY_EVENT,
  ServerStateEvent,
  SocketReady,
  SocketServerEvents,
  TypingEvent,
  UserEvent,
} from '@sd/contracts';
import { io } from 'socket.io-client';
import type { z } from 'zod';
import { readCsrfCookie } from './http';
import type { Transport } from './platform';

export interface RealtimeHandlers {
  onConnect(): void;
  /**
   * `retrying`: the transport reconnects by itself. After a server-side
   * disconnect (shutdown, revoked session) it does not, and only a new
   * `connect()` brings the socket back.
   */
  onDisconnect(reason: string, retrying: boolean): void;
  /** The handshake failed. Without `retrying` the server refused it (it only says `unauthorized`). */
  onConnectError(message: string, retrying: boolean): void;
  onReady(ready: SocketReadyPayload): void;
  onConversationEvent(event: ConversationEvent): void;
  onUserEvent(event: UserEvent): void;
  onPresence(event: PresenceEvent): void;
  onServerState(event: ServerStateEvent): void;
  onTyping(event: TypingEvent): void;
}

export interface RealtimeConnection {
  /** Connects now, also when the transport is waiting before its next retry. */
  connect(): void;
  disconnect(): void;
  readonly connected: boolean;
}

export type RealtimeFactory = (transport: Transport, handlers: RealtimeHandlers) => RealtimeConnection;

type SocketReadyPayload = z.infer<typeof SocketReady>;

/** Delay before reconnecting a socket the transport gave up on: 1 s doubling to 15 s, ±50 % so a restart does not bring every client back at once. */
export function reconnectDelayMs(attempt: number, random: () => number = Math.random): number {
  return Math.round(Math.min(15_000, 1000 * 2 ** attempt) * (0.5 + random()));
}

/**
 * Socket.IO client for one server. Every payload is validated against the
 * contract; malformed frames are dropped. Desktop passes a fresh short-lived
 * token on each (re)connect; the web relies on the session cookie. The default
 * transports start with long-polling and upgrade to WebSocket, so a proxy that
 * blocks WebSocket still gets realtime.
 */
export const socketIoRealtime: RealtimeFactory = (transport, handlers) => {
  const socket = io(transport.baseUrl, {
    path: SOCKET_PATH,
    autoConnect: false,
    withCredentials: transport.auth.kind === 'cookie',
    reconnectionDelay: 1000,
    reconnectionDelayMax: 15_000,
    auth: (cb) => {
      // Web: a long-polling handshake has no Origin header; the CSRF token stands in for it.
      if (transport.auth.kind !== 'bearer') {
        const csrf = readCsrfCookie();
        return cb(csrf ? { csrf } : {});
      }
      transport.auth
        .getToken()
        .then((token) => cb(token ? { token } : {}))
        .catch(() => cb({}));
    },
  });

  const guard =
    <T>(schema: { safeParse(v: unknown): { success: true; data: T } | { success: false } }, handler: (value: T) => void) =>
    (payload: unknown) => {
      const parsed = schema.safeParse(payload);
      if (parsed.success) handler(parsed.data);
    };

  socket.on('connect', () => handlers.onConnect());
  // Socket.IO clears `active` when it stopped retrying (server-side disconnect, refused handshake).
  socket.on('disconnect', (reason) => handlers.onDisconnect(reason, socket.active));
  socket.on('connect_error', (error) => handlers.onConnectError(error?.message ?? '', socket.active));
  socket.on(SOCKET_READY_EVENT, guard(SocketReady, handlers.onReady));
  socket.on(SocketServerEvents.conversationEvent, guard(ConversationEvent, handlers.onConversationEvent));
  socket.on(SocketServerEvents.userEvent, guard(UserEvent, handlers.onUserEvent));
  socket.on(SocketServerEvents.presence, guard(PresenceEvent, handlers.onPresence));
  socket.on(SocketServerEvents.serverState, guard(ServerStateEvent, handlers.onServerState));
  socket.on(SocketServerEvents.typing, guard(TypingEvent, handlers.onTyping));

  return {
    connect: () => {
      // Between its own retries (up to 15 s apart) Socket.IO ignores connect(); after a wake-up or
      // a network change that delay is pure waiting, so the attempt starts over now.
      if (socket.active && !socket.connected) socket.disconnect();
      socket.connect();
    },
    disconnect: () => socket.disconnect(),
    get connected() {
      return socket.connected;
    },
  };
};
